import { useState, useEffect, useMemo } from 'react';
import { ACCOUNT_TYPES, inferAccountType } from '../../constants/accountTypes';
import {
    ASSET_CLASSES,
    ASSET_CLASS_LABELS,
    USEFUL_LIFE_TABLE,
    ASSET_CATEGORIES,
    FIXED_ASSET_ACCOUNT_NAMES,
    guessCategory,
    classifyAsset,
    calculateDepreciation
} from '../../constants/assetClasses';
import { formatCurrency, parseAmount, generateId, formatAmountInput } from '../../utils/formatting';
import { formatDate, clampDate } from '../../utils/date';
import { printSection } from '../../utils/printHelper';

const API_URL = import.meta.env.VITE_API_URL;

export const FixedAssets = ({ accounts, journals, setJournals, showAlert, currentFiscalYear, fileSystem, accountType, orgType, user, activeBookEmail }) => {
    const dataEmail = activeBookEmail || user?.email;
    const [assets, setAssets] = useState([]);
    const [deletedAssets, setDeletedAssets] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('active');
    const isKojin = accountType === 'kojin';
    const isCorp = !isKojin;

    const defaultFormData = {
        name: '',
        category: 'パソコン',
        purchaseDate: new Date().toISOString().split('T')[0],
        acquisitionCost: '',
        usefulLife: 4,
        businessRatio: 100,
        memo: '',
        journalId: null
    };
    const [formData, setFormData] = useState(defaultFormData);

    useEffect(() => {
        const loadAssets = async () => {
            if (!dataEmail) return;
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}?action=getAssets&type=${accountType === 'kojin' ? 'kojin' : 'houjin'}&user=${encodeURIComponent(dataEmail)}`);
                if (response.ok) {
                    const data = await response.json();
                    setAssets(data.assets || []);
                    setDeletedAssets(data.deletedAssets || []);
                }
            } catch (err) {
                console.error('Failed to load assets:', err);
            } finally {
                setIsLoading(false);
            }
        };
        loadAssets();
    }, [accountType, dataEmail]);

    const saveAssetsData = async (newAssets, newDeletedAssets = deletedAssets) => {
        if (!dataEmail) return false;
        try {
            const response = await fetch(`${API_URL}?action=saveAssets&type=${accountType === 'kojin' ? 'kojin' : 'houjin'}&user=${encodeURIComponent(user.email)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assets: newAssets, deletedAssets: newDeletedAssets })
            });
            return response.ok;
        } catch (err) {
            console.error('Failed to save assets:', err);
            return false;
        }
    };

    const saveAssets = (newAssets) => saveAssetsData(newAssets, deletedAssets);

    const unregisteredJournals = useMemo(() => {
        const fixedAssetAccountIds = accounts
            .filter(a => FIXED_ASSET_ACCOUNT_NAMES.some(name => a.name.includes(name)))
            .map(a => a.id);

        const registeredJournalIds = new Set([
            ...assets.map(a => a.journalId).filter(Boolean),
            ...deletedAssets.map(a => a.journalId).filter(Boolean)
        ]);

        return journals.filter(journal => {
            if (journal.isClosingEntry) return false;
            if (registeredJournalIds.has(journal.id)) return false;

            return journal.lines.some(line =>
                line.type === 'debit' && fixedAssetAccountIds.includes(line.accountId)
            );
        });
    }, [journals, assets, deletedAssets, accounts]);

    const handleCategoryChange = (category) => {
        setFormData({
            ...formData,
            category,
            usefulLife: USEFUL_LIFE_TABLE[category] || 4
        });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.acquisitionCost) {
            showAlert('資産名と取得価額は必須です', 'error');
            return;
        }

        const cost = parseAmount(formData.acquisitionCost);
        const assetClass = classifyAsset(cost, isKojin, isCorp);

        const assetData = {
            id: editingAsset?.id || generateId(),
            name: formData.name,
            category: formData.category,
            purchaseDate: formData.purchaseDate,
            acquisitionCost: cost,
            usefulLife: formData.usefulLife,
            depreciationMethod: 'straight-line',
            businessRatio: formData.businessRatio,
            assetClass,
            depreciationHistory: editingAsset?.depreciationHistory || [],
            isDisposed: false,
            memo: formData.memo,
            journalId: formData.journalId || editingAsset?.journalId || null,
            createdAt: editingAsset?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        let newAssets;
        if (editingAsset) {
            newAssets = assets.map(a => a.id === editingAsset.id ? assetData : a);
        } else {
            newAssets = [...assets, assetData];
        }

        const success = await saveAssets(newAssets);
        if (success) {
            setAssets(newAssets);
            setShowModal(false);
            setEditingAsset(null);
            setFormData(defaultFormData);
            showAlert(editingAsset ? '資産を更新しました' : '資産を登録しました');
        } else {
            showAlert('保存に失敗しました', 'error');
        }
    };

    const handleDelete = async (asset) => {
        if (!confirm(`「${asset.name}」を削除してよろしいですか？\n（削除済みタブから復元可能です）`)) return;

        const deletedRecord = {
            ...asset,
            deletedAt: new Date().toISOString()
        };

        const newAssets = assets.filter(a => a.id !== asset.id);
        const newDeletedAssets = [...deletedAssets, deletedRecord];

        const success = await saveAssetsData(newAssets, newDeletedAssets);
        if (success) {
            setAssets(newAssets);
            setDeletedAssets(newDeletedAssets);
            showAlert('資産を削除しました');
        } else {
            showAlert('削除に失敗しました', 'error');
        }
    };

    const handleRestore = async (asset) => {
        const { deletedAt, ...restoredAsset } = asset;
        restoredAsset.updatedAt = new Date().toISOString();

        const newDeletedAssets = deletedAssets.filter(a => a.id !== asset.id);
        const newAssets = [...assets, restoredAsset];

        const success = await saveAssetsData(newAssets, newDeletedAssets);
        if (success) {
            setAssets(newAssets);
            setDeletedAssets(newDeletedAssets);
            showAlert('資産を復元しました');
            setActiveTab('active');
        } else {
            showAlert('復元に失敗しました', 'error');
        }
    };

    const openFromJournal = (journal) => {
        const fixedAssetAccountIds = accounts
            .filter(a => FIXED_ASSET_ACCOUNT_NAMES.some(name => a.name.includes(name)))
            .map(a => a.id);

        const assetLine = journal.lines.find(line =>
            line.type === 'debit' && fixedAssetAccountIds.includes(line.accountId)
        );

        if (!assetLine) return;

        const account = accounts.find(a => a.id === assetLine.accountId);
        const category = guessCategory(account?.name || '', journal.description);

        setFormData({
            name: journal.description,
            category,
            purchaseDate: journal.date,
            acquisitionCost: assetLine.amount,
            usefulLife: USEFUL_LIFE_TABLE[category] || 4,
            businessRatio: 100,
            memo: '',
            journalId: journal.id
        });
        setEditingAsset(null);
        setShowModal(true);
    };

    const currentYearDepreciation = useMemo(() => {
        return assets.reduce((total, asset) => {
            if (asset.isDisposed) return total;
            return total + calculateDepreciation(asset, currentFiscalYear, isKojin ? 12 : 3);
        }, 0);
    }, [assets, currentFiscalYear, isKojin]);

    const createDepreciationJournals = () => {
        const depreciationAccountId = accounts.find(a => a.name === '減価償却費')?.id || '760';
        const accumulatedAccountId = accounts.find(a => a.name === '減価償却累計額')?.id || '122';

        const newJournals = [];
        assets.forEach(asset => {
            if (asset.isDisposed) return;
            const amount = calculateDepreciation(asset, currentFiscalYear, isKojin ? 12 : 3);
            if (amount <= 0) return;

            const alreadyExists = asset.depreciationHistory?.some(h => h.fiscalYear === currentFiscalYear);
            if (alreadyExists) return;

            const journalId = generateId();
            newJournals.push({
                id: journalId,
                date: `${currentFiscalYear}-${isKojin ? '12' : '03'}-31`,
                description: `減価償却 ${asset.name}`,
                lines: [
                    { id: generateId(), type: 'debit', accountId: depreciationAccountId, amount },
                    { id: generateId(), type: 'credit', accountId: accumulatedAccountId, amount }
                ],
                isClosingEntry: true,
                closingType: 'depreciation',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            asset.depreciationHistory = asset.depreciationHistory || [];
            asset.depreciationHistory.push({ fiscalYear: currentFiscalYear, amount, journalId });
        });

        if (newJournals.length === 0) {
            showAlert('作成する償却仕訳がありません', 'warning');
            return;
        }

        setJournals([...journals, ...newJournals]);
        saveAssets(assets);
        showAlert(`${newJournals.length}件の減価償却仕訳を作成しました`);
    };

    const startEdit = (asset) => {
        setEditingAsset(asset);
        setFormData({
            name: asset.name,
            category: asset.category,
            purchaseDate: asset.purchaseDate,
            acquisitionCost: asset.acquisitionCost,
            usefulLife: asset.usefulLife,
            businessRatio: asset.businessRatio,
            memo: asset.memo || '',
            journalId: asset.journalId || null
        });
        setShowModal(true);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>資産データを読み込んでいます...</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h2>固定資産台帳</h2>
                    <p style={{ color: '#666' }}>{currentFiscalYear}年{isKojin ? '' : '度'}</p>
                </div>
                <button className="btn btn-primary" onClick={() => { setEditingAsset(null); setFormData(defaultFormData); setShowModal(true); }}>
                    + 資産を登録
                </button>
            </div>

            <div className="card" style={{ marginBottom: '24px', background: '#e3f2fd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3>今期の減価償却費</h3>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', margin: '8px 0' }}>{formatCurrency(currentYearDepreciation)} 円</p>
                    </div>
                    <button className="btn btn-primary" onClick={createDepreciationJournals}>
                        減価償却仕訳を作成
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
                <button
                    className={`btn ${activeTab === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('active')}
                >
                    資産一覧 ({assets.length})
                </button>
                <button
                    className={`btn ${activeTab === 'unregistered' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('unregistered')}
                    style={{ position: 'relative' }}
                >
                    仕訳から追加
                    {unregisteredJournals.length > 0 && (
                        <span style={{
                            background: '#f44336',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '2px 6px',
                            fontSize: '11px',
                            marginLeft: '4px'
                        }}>{unregisteredJournals.length}</span>
                    )}
                </button>
                <button
                    className={`btn ${activeTab === 'deleted' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('deleted')}
                >
                    削除済み ({deletedAssets.length})
                </button>
            </div>

            {activeTab === 'active' && (
                assets.length === 0 ? (
                    <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>登録された資産はありません</p>
                ) : (
                    <>
                        <div id="fixed-assets-print-section">
                            <h2 style={{marginBottom: '12px', fontSize: '16px', display: 'none'}} className="print-only">有形固定資産及び無形固定資産の明細 — {currentFiscalYear}年度</h2>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>資産名</th>
                                        <th>カテゴリ</th>
                                        <th>取得日</th>
                                        <th style={{ textAlign: 'right' }}>取得価額</th>
                                        <th>区分</th>
                                        <th style={{ textAlign: 'right' }}>今期償却額</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assets.map(asset => (
                                        <tr key={asset.id} style={asset.isDisposed ? { opacity: 0.5 } : {}}>
                                            <td>{asset.name}</td>
                                            <td>{asset.category}</td>
                                            <td>{formatDate(asset.purchaseDate)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(asset.acquisitionCost)}</td>
                                            <td><span className="badge">{ASSET_CLASS_LABELS[asset.assetClass]}</span></td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(calculateDepreciation(asset, currentFiscalYear, isKojin ? 12 : 3))}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button className="btn btn-small" onClick={() => startEdit(asset)}>編集</button>
                                                <button className="btn btn-small btn-danger" style={{ marginLeft: '4px' }} onClick={() => handleDelete(asset)}>削除</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button
                            onClick={() => printSection('fixed-assets-print-section')}
                            style={{marginTop: '12px', padding: '8px 16px', background: '#607d8b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                        >
                            附属明細書を印刷 / PDF
                        </button>
                    </>
                )
            )}

            {activeTab === 'unregistered' && (
                unregisteredJournals.length === 0 ? (
                    <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>
                        未登録の固定資産仕訳はありません
                    </p>
                ) : (
                    <div>
                        <p style={{ color: '#666', marginBottom: '16px' }}>
                            以下の仕訳に固定資産科目（{FIXED_ASSET_ACCOUNT_NAMES.join('、')}）が含まれています。
                            「登録」ボタンで固定資産台帳に追加できます。
                        </p>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>日付</th>
                                    <th>摘要</th>
                                    <th>科目</th>
                                    <th style={{ textAlign: 'right' }}>金額</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {unregisteredJournals.map(journal => {
                                    const fixedAssetAccountIds = accounts
                                        .filter(a => FIXED_ASSET_ACCOUNT_NAMES.some(name => a.name.includes(name)))
                                        .map(a => a.id);
                                    const assetLine = journal.lines.find(line =>
                                        line.type === 'debit' && fixedAssetAccountIds.includes(line.accountId)
                                    );
                                    const account = accounts.find(a => a.id === assetLine?.accountId);
                                    return (
                                        <tr key={journal.id}>
                                            <td>{formatDate(journal.date)}</td>
                                            <td>{journal.description}</td>
                                            <td>{account?.name || '-'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(assetLine?.amount || 0)}</td>
                                            <td>
                                                <button className="btn btn-small btn-primary" onClick={() => openFromJournal(journal)}>
                                                    登録
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {activeTab === 'deleted' && (
                deletedAssets.length === 0 ? (
                    <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>削除済みの資産はありません</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>資産名</th>
                                <th>カテゴリ</th>
                                <th>取得日</th>
                                <th style={{ textAlign: 'right' }}>取得価額</th>
                                <th>削除日時</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {deletedAssets.map(asset => (
                                <tr key={asset.id} style={{ opacity: 0.7 }}>
                                    <td>{asset.name}</td>
                                    <td>{asset.category}</td>
                                    <td>{formatDate(asset.purchaseDate)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatCurrency(asset.acquisitionCost)}</td>
                                    <td>{new Date(asset.deletedAt).toLocaleString('ja-JP')}</td>
                                    <td>
                                        <button className="btn btn-small btn-primary" onClick={() => handleRestore(asset)}>
                                            復元
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{editingAsset ? '資産を編集' : '資産を登録'}</h3>
                        <div className="form-group">
                            <label>資産名 *</label>
                            <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>カテゴリ</label>
                            <select value={formData.category} onChange={e => handleCategoryChange(e.target.value)}>
                                {ASSET_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>取得日</label>
                            <input type="date" value={formData.purchaseDate} onChange={e => setFormData({ ...formData, purchaseDate: clampDate(e.target.value) })} />
                        </div>
                        <div className="form-group">
                            <label>取得価額 *</label>
                            <input type="text" value={formatAmountInput(formData.acquisitionCost)} onChange={e => setFormData({ ...formData, acquisitionCost: parseAmount(e.target.value) })} />
                            {formData.acquisitionCost && (
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    区分: {ASSET_CLASS_LABELS[classifyAsset(parseAmount(formData.acquisitionCost), isKojin, isCorp)]}
                                </p>
                            )}
                        </div>
                        <div className="form-group">
                            <label>耐用年数</label>
                            <input type="number" value={formData.usefulLife} onChange={e => setFormData({ ...formData, usefulLife: parseInt(e.target.value) || 4 })} />
                        </div>
                        {isKojin && (
                            <div className="form-group">
                                <label>事業使用割合 (%)</label>
                                <input type="number" value={formData.businessRatio} onChange={e => setFormData({ ...formData, businessRatio: parseInt(e.target.value) || 100 })} min="0" max="100" />
                            </div>
                        )}
                        <div className="form-group">
                            <label>メモ</label>
                            <textarea value={formData.memo} onChange={e => setFormData({ ...formData, memo: e.target.value })} rows="2" />
                        </div>
                        <div className="btn-group">
                            <button className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingAsset(null); }}>キャンセル</button>
                            <button className="btn btn-primary" onClick={handleSubmit}>{editingAsset ? '更新' : '登録'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FixedAssets;
